import React, { useMemo, useState } from 'react';
import FullRecord from '../../../../pages/screening/FullRecord';
import { Modal } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DismissHitModal from '../../../BiteDB/Modal/DismissHitModal';
import { getEntityMatchScore } from 'utils/nameMatchScore';
import {
  formatEntityLensCode,
  getEntityLensBadgeClassName,
  parseEntityLensNames,
} from 'utils/entityLensBadge';

interface IRowProps {
  index: number;
  result: any;
  selected: string;
  handleVisClick: any;
  isProduct?: boolean;
  showMatchScore?: boolean;
  matchTerm?: string;
  showBulkSelection?: boolean;
  isBulkSelected?: boolean;
  onToggleBulkSelection?: (result: any) => void;
  isBulkSelectionDisabled?: boolean;
  layout?: 'default' | 'entityHits';
}

const getScoreBand = (score: number) => {
  if (score >= 90) return 'green';
  if (score >= 75) return 'yellow';
  if (score >= 55) return 'orange';
  return 'red';
};

const Level3Row: React.FC<IRowProps> = ({
  result,
  selected,
  handleVisClick,
  isProduct = false,
  showMatchScore = false,
  matchTerm = '',
  showBulkSelection = false,
  isBulkSelected = false,
  onToggleBulkSelection,
  isBulkSelectionDisabled = false,
  layout = 'default',
}) => {
  const [showFullRecord, setShowFullRecord] = useState({
    show: false,
    record: {},
  });
  const [showDismissModal, setShowDismissModal] = useState(false);

  const matchScore = useMemo(
    () =>
      showMatchScore && !isProduct
        ? getEntityMatchScore(matchTerm, result)
        : null,
    [isProduct, matchTerm, result, showMatchScore],
  );

  const hitLenses = useMemo(
    () =>
      layout === 'entityHits' && !isProduct ? parseEntityLensNames(result) : [],
    [isProduct, layout, result],
  );

  const recordName = result['Name of Entity'] || result['Product Name'];

  const handleDismiss = (reason: string) => {
    handleVisClick(result['BITE Record ID'], reason, recordName);
    result.removed = true;
    setShowDismissModal(false);
  };

  const handleShowFullRecord = (fullHit: any) => {
    const obj = {
      _source: fullHit,
      matched_queries: [selected],
    };

    setShowFullRecord({ show: true, record: obj });
  };

  return (
    <>
      <div
        className={`view-history-body-frame-row ${
          result.removed ? 'deactivated-row' : ''
        }`}
      >
        {showBulkSelection && (
          <div className='view-history-body-frame-row-select bulk-hit-select-cell'>
            <Checkbox
              checked={isBulkSelected}
              disabled={isBulkSelectionDisabled}
              onChange={() => onToggleBulkSelection?.(result)}
              size='small'
              inputProps={{
                'aria-label': `Select ${recordName || 'screening hit'}`,
              }}
              sx={{ color: 'var(--text-secondary)', padding: 0 }}
            />
          </div>
        )}
        {result.removed ? (
          <div className='view-history-body-frame-row-dates'>
            <div className='view-history-body-frame-row-date-text'>
              {result['Name of Entity']
                ? result['Name of Entity']
                : result['Product Name']}
            </div>
          </div>
        ) : (
          <div
            className='view-history-body-frame-row-name'
            onClick={() => handleShowFullRecord(result)}
          >
            <div className='view-history-body-frame-row-date-text-names'>
              {result['Name of Entity']
                ? result['Name of Entity']
                : result['Product Name']}
            </div>
          </div>
        )}
        {isProduct && (
          <div className='view-history-body-frame-row-dates'>
            <div className='view-history-body-frame-row-date-text'>
              {result['HS Code'] ? result['HS Code'] : ''}
            </div>
          </div>
        )}
        {!isProduct && (
          <div className='view-history-body-frame-row-dates'>
            <div className='view-history-body-frame-row-date-text'>
              {result['Entity Country'] ||
                result['Entity Country iso-2 Code'] ||
                ''}
            </div>
          </div>
        )}
        {layout === 'entityHits' && !isProduct && (
          <div className='view-history-body-frame-row-dates'>
            {hitLenses.length === 0 ? (
              <span className='entity-lens-badge'>--</span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {hitLenses.map((lens) => {
                  const lensCode = formatEntityLensCode(lens);
                  return (
                    <Tooltip
                      key={lens}
                      title={lens || 'No BITE Lens'}
                      arrow
                      placement='top'
                    >
                      <span className={getEntityLensBadgeClassName(lensCode)}>
                        {lensCode}
                      </span>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {layout === 'entityHits' && !isProduct && (
          <div className='view-history-body-frame-row-dates'>
            <div className='view-history-body-frame-row-date-text'>
              {result['List Name']}
            </div>
          </div>
        )}
        <div className='view-history-body-frame-row-dates'>
          <div className='view-history-body-frame-row-date-text'>
            {result['Source Country'] ? result['Source Country'] : ''}
          </div>
        </div>
        {layout !== 'entityHits' && (
          <div className='view-history-body-frame-row-dates'>
            <div className='view-history-body-frame-row-date-text'>
              {result.Agency}
            </div>
          </div>
        )}
        {layout !== 'entityHits' && (
          <div className='view-history-body-frame-row-dates'>
            <div className='view-history-body-frame-row-date-text'>
              {result['List Name']}
            </div>
          </div>
        )}
        {showMatchScore && !isProduct && (
          <div className='view-history-body-frame-row-dates'>
            {matchScore === null ? (
              <span className='entity-score-empty'>—</span>
            ) : (
              <div className='entity-score-cell'>
                <span
                  className={`entity-score-line ${getScoreBand(matchScore)}`}
                />
                <span className='entity-score-value'>{matchScore}%</span>
              </div>
            )}
          </div>
        )}
        <div className='view-history-body-frame-row-dates'>
          <div className='view-history-body-frame-row-date-text'>
            {result['Date Updated on BITE Platform']}
          </div>
        </div>
        {result.removed ? (
          <div className='view-history-body-frame-row-icon'>
            <VisibilityOffIcon
              onClick={() => {
                handleVisClick(result['BITE Record ID'], undefined, recordName);
                result.removed = false;
              }}
            />
          </div>
        ) : (
          <div className='view-history-body-frame-row-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
              cursor={'pointer'}
              onClick={() => setShowDismissModal(true)}
            >
              <path
                d='M1.81509 9.53488C1.71295 9.37315 1.66188 9.29229 1.63329 9.16756C1.61182 9.07387 1.61182 8.92613 1.63329 8.83244C1.66188 8.70771 1.71295 8.62685 1.81509 8.46512C2.65915 7.12863 5.17155 3.75 9.0003 3.75C12.8291 3.75 15.3415 7.12863 16.1855 8.46512C16.2877 8.62685 16.3387 8.70771 16.3673 8.83244C16.3888 8.92613 16.3888 9.07387 16.3673 9.16756C16.3387 9.29229 16.2877 9.37315 16.1855 9.53488C15.3415 10.8714 12.8291 14.25 9.0003 14.25C5.17155 14.25 2.65915 10.8714 1.81509 9.53488Z'
                stroke='var(--text-primary)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M9.0003 11.25C10.2429 11.25 11.2503 10.2426 11.2503 9C11.2503 7.75736 10.2429 6.75 9.0003 6.75C7.75766 6.75 6.7503 7.75736 6.7503 9C6.7503 10.2426 7.75766 11.25 9.0003 11.25Z'
                stroke='var(--text-primary)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        )}
      </div>
      <Modal
        open={showFullRecord.show}
        onClose={() => setShowFullRecord({ show: false, record: {} })}
      >
        <FullRecord
          setShowFullRecord={setShowFullRecord}
          showFullRecord={showFullRecord}
        />
      </Modal>
      <DismissHitModal
        open={showDismissModal}
        recordName={recordName}
        onClose={() => setShowDismissModal(false)}
        onConfirm={handleDismiss}
      />
    </>
  );
};

export default Level3Row;
